import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { Users, UserPlus, Trash2, Mail, Shield, Loader2, CheckCircle2, ExternalLink, Eye, FileText } from 'lucide-react';

const API = window.__WR_API_BASE__;

export const TeamSettings = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');

  const fetchMembers = async () => {
    try {
      const response = await fetch(`${API}/api/team/my-team-members?days=7`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMembers(data.members || []);
      }
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchMembers();
    }
  }, [isOpen]);

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setAdding(true);
    try {
      const response = await fetch(`${API}/api/team/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          member_email: newEmail.trim(),
          member_name: newName.trim() || null,
          role: 'medic'
        })
      });

      if (response.ok) {
        toast.success(`Added ${newEmail} to your team!`);
        setNewEmail('');
        setNewName('');
        fetchMembers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to add team member');
      }
    } catch (error) {
      toast.error('Failed to add team member');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (email) => {
    if (!window.confirm(`Remove ${email} from your team?`)) return;

    try {
      const response = await fetch(`${API}/api/team/remove/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`Removed ${email} from team`);
        fetchMembers();
      } else {
        toast.error('Failed to remove team member');
      }
    } catch (error) {
      toast.error('Failed to remove team member');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Team Members
          </DialogTitle>
          <DialogDescription>
            Add medics who can always access your patient history - no share links needed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Add Member Form */}
          <form onSubmit={handleAddMember} className="space-y-3">
            <div className="flex gap-2">
              <div className="flex-1">
                <Label htmlFor="email" className="text-xs text-muted-foreground">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="medic@army.mil"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="w-32">
                <Label htmlFor="name" className="text-xs text-muted-foreground">Name (optional)</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Tyron"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>
            <Button type="submit" disabled={adding} className="w-full bg-green-600 hover:bg-green-700">
              {adding ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding...</>
              ) : (
                <><UserPlus className="w-4 h-4 mr-2" />Add Team Member</>
              )}
            </Button>
          </form>

          {/* Current Members */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Current Team</Label>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : members.length === 0 ? (
              <Card className="bg-secondary/50">
                <CardContent className="py-6 text-center">
                  <Users className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">No team members yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Add a medic above to get started</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {members.map((member) => (
                  <Card key={member.member_email} className="bg-card">
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${member.has_account ? 'bg-green-500/20' : 'bg-secondary'}`}>
                            <Shield className={`w-4 h-4 ${member.has_account ? 'text-green-400' : 'text-muted-foreground'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-sm flex items-center gap-2">
                              {member.member_name}
                              {!member.has_account && (
                                <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-400/50">
                                  Not signed up
                                </Badge>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">{member.member_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {member.has_account && member.recent_entries > 0 && (
                            <Badge variant="secondary" className="text-xs bg-blue-500/20 text-blue-400">
                              <FileText className="w-3 h-3 mr-1" />
                              {member.recent_entries} this week
                            </Badge>
                          )}
                          {member.has_account && member.member_user_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                onClose();
                                navigate(`/team/view/${member.member_user_id}`);
                              }}
                              className="h-7 text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                          )}
                          <Badge variant="secondary" className="text-xs capitalize">
                            {member.role}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMember(member.member_email)}
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* How it works */}
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="py-3 px-4">
              <p className="text-xs text-blue-400">
                <strong>Bi-directional access:</strong>
                <br/>• Your team members sign in → see a "Team" button → access YOUR dashboard
                <br/>• You can click "View" on any team member → see THEIR work
                <br/>• Same features for both: calendar, filters, checkboxes, notes
              </p>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Component for team members to see their provider dashboards
export const TeamDashboardSelector = ({ providers, onSelect }) => {
  if (!providers || providers.length === 0) return null;

  return (
    <Card className="border-green-500/30 bg-green-500/10">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-green-400">
          <Users className="w-4 h-4" />
          Team Dashboard Access
        </CardTitle>
        <CardDescription className="text-xs">
          You have access to these providers' dashboards
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {providers.map((provider) => (
          <Button
            key={provider.provider_id}
            variant="outline"
            className="w-full justify-between h-auto py-3 border-green-500/30 hover:bg-green-500/20"
            onClick={() => onSelect(provider)}
          >
            <div className="text-left">
              <p className="font-medium">{provider.provider_name}</p>
              <p className="text-xs text-muted-foreground">{provider.provider_email}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs capitalize">{provider.role}</Badge>
              <ExternalLink className="w-4 h-4" />
            </div>
          </Button>
        ))}
      </CardContent>
    </Card>
  );
};

export default TeamSettings;
